import React, { useState, useEffect } from 'react';
import { API_BASE, supabase } from '../../App'; // Import supabase for storage upload
import { toast } from 'react-toastify';

const AddMoneyModal = ({ session, onClose, onSuccess }) => {
    const [mode, setMode] = useState('manual'); // 'gateway' or 'manual'
    const [amount, setAmount] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [loading, setLoading] = useState(false);

    const [paymentMethods, setPaymentMethods] = useState([]);

    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const fetchPaymentMethods = async () => {
        try {
            const token = session.access_token;
            const response = await fetch(`${API_BASE}/payments/methods`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setPaymentMethods(data);
            }
        } catch (error) {
            console.error('Failed to load payment methods', error);
        }
    };

    const handleManualSubmit = async () => {
        if (!amount || amount < 10) {
            toast.error('Minimum amount is ₹10');
            return;
        }
        if (!transactionId) {
            toast.error('Please enter Transaction ID / UTR');
            return;
        }

        setLoading(true);
        try {
            let screenshotUrl = '';

            if (screenshot) {
                // 1. Upload Screenshot to Supabase Storage
                const fileExt = screenshot.name.split('.').pop();
                const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;
                const { data, error } = await supabase.storage
                    .from('payment_screenshots') // Ensure this bucket exists in Supabase
                    .upload(fileName, screenshot);

                if (error) {
                    console.error('Upload failed:', error);
                    toast.error('Failed to upload screenshot. Please try again.');
                    setLoading(false);
                    return;
                }

                // Get public URL
                const { data: publicUrlData } = supabase.storage
                    .from('payment_screenshots')
                    .getPublicUrl(fileName);

                screenshotUrl = publicUrlData.publicUrl;
            }

            // 2. Create Request in Backend
            const response = await fetch(`${API_BASE}/payments/manual`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    amount: parseFloat(amount),
                    transaction_id: transactionId,
                    screenshot_url: screenshotUrl
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Failed to submit request');
            }

            toast.success('Request submitted! Admin will verify shortly.');
            onSuccess(); // Close modal
        } catch (error) {
            console.error(error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGatewayPayment = async () => {
        // Existing Razorpay logic can go here or be triggered from parent
        toast.info("Razorpay Gateway integration is handled in WalletCard directly.");
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="modal-header">
                    <h3 className="modal-title">Add Money</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div style={{ padding: '10px 0' }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <button
                            className={`btn ${mode === 'manual' ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => setMode('manual')}
                            style={{ flex: 1 }}
                        >
                            Manual (UPI/Bank)
                        </button>
                    </div>

                    {mode === 'manual' && (
                        <div>
                            {/* Dynamic Payment Methods */}
                            {paymentMethods.length > 0 ? (
                                <div style={{
                                    background: 'var(--accent-bg)',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    textAlign: 'center'
                                }}>
                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '10px' }}>
                                        Scan QR or Pay to UPI/Bank
                                    </p>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {paymentMethods.map(method => (
                                            <div key={method.id} style={{ padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>
                                                {method.type === 'qr' && (
                                                    <div>
                                                        <div style={{
                                                            width: '150px',
                                                            height: '150px',
                                                            background: '#fff',
                                                            margin: '0 auto 10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <img src={method.details.qr_url} alt="QR" style={{ width: '100%', height: '100%' }} />
                                                        </div>
                                                        <p style={{ fontSize: '0.9rem' }}>{method.details.label}</p>
                                                    </div>
                                                )}
                                                {method.type === 'upi' && (
                                                    <div>
                                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>UPI ID</p>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                            <p style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{method.details.value}</p>
                                                            <button
                                                                className="btn btn-sm btn-outline"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(method.details.value);
                                                                    toast.success('Copied!');
                                                                }}
                                                            >
                                                                Copy
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {method.type === 'bank' && (
                                                    <div style={{ textAlign: 'left' }}>
                                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Bank Details</p>
                                                        <p style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{method.details.value}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                    Loading payment methods...
                                </div>
                            )}

                            <div className="form-group">
                                <label className="form-label">Amount (₹)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    placeholder="e.g. 100"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Transaction ID / UTR</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={transactionId}
                                    onChange={e => setTransactionId(e.target.value)}
                                    placeholder="Enter 12-digit UTR"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Upload Screenshot (Optional)</label>
                                <input
                                    type="file"
                                    className="form-input"
                                    accept="image/*"
                                    onChange={e => setScreenshot(e.target.files[0])}
                                />
                            </div>

                            <button
                                className="btn btn-success"
                                onClick={handleManualSubmit}
                                disabled={loading}
                                style={{ width: '100%', marginTop: '10px' }}
                            >
                                {loading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    )}

                    {mode === 'gateway' && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <p>Instant payment via Razorpay.</p>
                            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Use the "Recharge" button on Previous screen for Gateway.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddMoneyModal;
